import React, { useMemo } from 'react';
import { PaintPointLabelProps } from './VehiclePaintPoints.types';
import { getPaintStatusFromValue, paintStatusConfig, formatMeasurementRange } from '../VehiclePaintMeasurement/paintStatusConfig';

/**
 * Komponent etykiety punktu pomiarowego lakieru
 *
 * Składa się z:
 * 1. Okrągły badge z numerem (w prawym górnym rogu etykiety)
 * 2. Linia łącząca badge z etykietą
 * 3. Prostokątna etykieta z zakresem wartości
 *
 * Layout:
 *        ●12  ← Badge z numerem
 *        │
 *   ┌────────┐
 *   │ 0 - 50 │ ← Etykieta z zakresem
 *   └────────┘
 */
const HIGHLIGHT_COLOR = '#0064FF';

export const PaintPointLabel: React.FC<PaintPointLabelProps> = ({
  pointNumber,
  value,
  x,
  y,
  position = 'right',
  labelOverride,
  hideLabel = false,
  minMeasurement,
  maxMeasurement,
  isHighlighted = false,
  onHover,
  onClick,
}) => {
  // Użyj labelOverride jeśli dostępny, w przeciwnym razie pointNumber
  const displayNumber = labelOverride || pointNumber;
  // Określ status na podstawie wartości
  const status = useMemo(() => getPaintStatusFromValue(value), [value]);

  // Pobierz konfigurację koloru dla statusu
  const config = paintStatusConfig[status];

  // Oblicz offset etykiety w zależności od pozycji
  const labelOffset = useMemo(() => {
    const distance = 25; // odległość etykiety od badge w px
    switch (position) {
      case 'top':
        return { x: 0, y: -distance };
      case 'bottom':
        return { x: 0, y: distance };
      case 'left':
        return { x: -distance, y: 0 };
      case 'right':
      default:
        return { x: distance, y: 0 };
    }
  }, [position]);

  // Oblicz pozycję linii łączącej (od punktu markera do etykiety)
  const lineStyle = useMemo(() => {
    const markerRadius = 4; // promień małego okręgu (marker)
    const lineLength = Math.abs(labelOffset.x || labelOffset.y);

    switch (position) {
      case 'top':
        return {
          width: '2px',
          height: `${lineLength - markerRadius}px`,
          left: '0px',
          bottom: `${markerRadius}px`,
          transform: 'translateX(-50%)',
        };
      case 'bottom':
        return {
          width: '2px',
          height: `${lineLength - markerRadius}px`,
          left: '0px',
          top: `${markerRadius}px`,
          transform: 'translateX(-50%)',
        };
      case 'left':
        return {
          width: `${lineLength - markerRadius}px`,
          height: '2px',
          right: `${markerRadius}px`,
          top: '0px',
          transform: 'translateY(-50%)',
        };
      case 'right':
      default:
        return {
          width: `${lineLength - markerRadius}px`,
          height: '2px',
          left: `${markerRadius}px`,
          top: '0px',
          transform: 'translateY(-50%)',
        };
    }
  }, [position, labelOffset]);

  // Oblicz pozycję badge (odwrotnie do pozycji etykiety)
  const badgePosition = useMemo(() => {
    const badgeOffset = 16; // odległość badge od etykiety w px
    const badgeVerticalOffset = 14; // pionowa odległość badge od etykiety w px

    switch (position) {
      case 'top':
        // Etykieta na górze → badge na dole
        return {
          right: `-${badgeOffset}px`,
          top: `-${badgeVerticalOffset}px`,
        };
      case 'bottom':
        // Etykieta na dole → badge na górze
        return {
          right: `-${badgeOffset}px`,
          top: `-${badgeVerticalOffset}px`,
        };
      case 'left':
        // Etykieta po lewej → badge po prawej
        return {
          left: `-${badgeOffset}px`,
          top: `-${badgeVerticalOffset}px`,
        };
      case 'right':
      default:
        // Etykieta po prawej → badge po lewej
        return {
          right: `-${badgeOffset}px`,
          top: `-${badgeVerticalOffset}px`,
        };
    }
  }, [position]);

  const isInteractive = Boolean(onHover || onClick);
  const strokeColor = isHighlighted ? HIGHLIGHT_COLOR : config.strokeColor;

  return (
    <div
      className={isInteractive ? 'absolute cursor-pointer' : 'absolute pointer-events-none'}
      style={{
        left: `${x}%`,
        top: `${y}%`,
        zIndex: isHighlighted ? 20 : undefined,
      }}
      onMouseEnter={onHover ? () => onHover(String(pointNumber)) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
      onClick={onClick ? () => onClick(String(pointNumber)) : undefined}
    >
      {/* Główny punkt (marker - mały okrąg) */}
      <div
        className="absolute rounded-full transition-colors duration-200"
        style={{
          width: '8px',
          height: '8px',
          backgroundColor: isHighlighted ? HIGHLIGHT_COLOR : config.fillColor,
          left: '-4px',
          top: '-4px',
          zIndex: 3,
        }}
      />

      {/* Renderuj etykietę tylko jeśli hideLabel === false */}
      {!hideLabel && (
        <>
          {/* Linia łącząca marker z etykietą */}
          <div
            className="absolute transition-colors duration-200"
            style={{
              ...lineStyle,
              backgroundColor: strokeColor,
              zIndex: 1,
            }}
          />

          {/* Etykieta z zakresem wartości */}
          <div
            className="absolute whitespace-nowrap"
            style={{
              left: `${labelOffset.x}px`,
              top: `${labelOffset.y}px`,
              transform:
                position === 'left'
                  ? 'translate(-100%, -50%)'
                  : position === 'right'
                    ? 'translate(0, -50%)'
                    : position === 'top'
                      ? 'translate(-50%, -100%)'
                      : 'translate(-50%, 0)',
            }}
          >
            {/* Badge z numerem */}
            <div
              className="flex absolute justify-center items-center font-bold text-white rounded-full shadow-lg"
              style={{
                width: '28px',
                height: '28px',
                fontSize: '11px',
                backgroundColor: config.strokeColor,
                border: `2px solid ${config.strokeColor}`,
                ...badgePosition,
                zIndex: 2,
              }}
            >
              {displayNumber}
            </div>

            {/* Prostokątna etykieta */}
            <div
              className="relative flex items-center justify-center px-3 py-1.5 rounded shadow-lg transition-[border-color,box-shadow] duration-200"
              style={{
                backgroundColor: '#111438',
                border: `2px solid ${strokeColor}`,
                minWidth: '60px',
                boxShadow: isHighlighted ? `0 0 20px 0 ${HIGHLIGHT_COLOR}` : undefined,
              }}
            >
              <span className="text-xs font-semibold text-white select-none">
                {value !== undefined
                  ? (minMeasurement != null && maxMeasurement != null
                    ? formatMeasurementRange(minMeasurement, maxMeasurement)
                    : config.range)
                  : 'BRAK'}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Accessibility */}
      <div className="sr-only">
        Punkt {displayNumber}: {value !== undefined ? `${value} μm` : 'brak pomiaru'}
      </div>
    </div>
  );
};
